import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { User, LogOut, Home, Users, Briefcase, Settings, ChevronDown, Menu, X } from 'lucide-react';
import logo from '../images/RenderSpaceNT.png';

const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsMobileMenuOpen(false);
  };

  const handleProfileClick = () => {
    setIsProfileOpen(!isProfileOpen);
  };

  const handleEditProfile = () => {
    setIsProfileOpen(false);
    setIsMobileMenuOpen(false);
    navigate('/edit-profile');
  };

  const handleViewProfile = () => {
    setIsProfileOpen(false);
    setIsMobileMenuOpen(false);
    if (user?.userType === 'freelancer') {
      navigate('/freelancer-profile');
    } else {
      navigate('/client-profile');
    }
  };

  // Close dropdown when route changes
  useEffect(() => {
    setIsProfileOpen(false);
    setIsMobileMenuOpen(false);
  }, [navigate]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-black/20 backdrop-blur-md border-b border-white/10' 
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2 group">
              <img src={logo} alt="RenderSpace Logo" className="h-8 w-auto transition-transform group-hover:scale-105" />
              <span className="text-xl font-bold text-gradient hidden sm:block">RenderSpace</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            <Link
              to="/"
              className="text-white hover:text-secondary-400 px-3 py-2 rounded-md text-sm font-medium flex items-center space-x-1 transition-all duration-300 hover:bg-white/10"
            >
              <Home size={16} />
              <span>Home</span>
            </Link>
            {user?.userType === 'freelancer' && (
              <Link
                to="/projects"
                className="text-white hover:text-secondary-400 px-3 py-2 rounded-md text-sm font-medium flex items-center space-x-1 transition-all duration-300 hover:bg-white/10"
              >
                <Briefcase size={16} />
                <span>Find Projects</span>
              </Link>
            )}
            {user?.userType === 'client' && (
              <>
                <Link
                  to="/post-project"
                  className="text-white hover:text-secondary-400 px-3 py-2 rounded-md text-sm font-medium flex items-center space-x-1 transition-all duration-300 hover:bg-white/10"
                >
                  <Briefcase size={16} />
                  <span>Post Project</span>
                </Link>
                <Link
                  to="/client-projects"
                  className="text-white hover:text-secondary-400 px-3 py-2 rounded-md text-sm font-medium flex items-center space-x-1 transition-all duration-300 hover:bg-white/10"
                >
                  <Briefcase size={16} />
                  <span>My Projects</span>
                </Link>
              </>
            )}
          </nav>

          {/* Desktop User Actions */}
          <div className="hidden md:flex items-center space-x-4">
            {user ? (
              <div className="flex items-center space-x-3">
                {/* Profile Dropdown */}
                <div className="relative" ref={profileRef}>
                  <button
                    onClick={handleProfileClick}
                    className="flex items-center space-x-2 text-sm text-white/80 hover:text-secondary-400 px-3 py-2 rounded-md font-medium transition-all duration-300 hover:bg-white/5"
                  >
                    <User size={16} />
                    <span className="hidden lg:block">{user.name}</span>
                    <span className="text-xs bg-secondary-400/20 text-secondary-400 px-2 py-1 rounded-full border border-secondary-400/30">
                      {user.userType}
                    </span>
                    <ChevronDown size={14} className={`transition-transform duration-300 ${isProfileOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* Profile Dropdown Menu */}
                  {isProfileOpen && (
                    <>
                      {/* Backdrop */}
                      <div className="fixed inset-0 z-40" onClick={() => setIsProfileOpen(false)} />
                      <div className="absolute right-0 mt-2 w-48 bg-black/80 backdrop-blur-md rounded-xl py-1 z-50 border border-white/10 shadow-2xl">
                        <button
                          onClick={handleViewProfile}
                          className="block w-full text-left px-4 py-2 text-sm text-white/80 hover:bg-white/5 hover:text-secondary-400 flex items-center space-x-2 transition-all duration-300"
                        >
                          <User size={14} />
                          <span>View Profile</span>
                        </button>
                        <button
                          onClick={handleEditProfile}
                          className="block w-full text-left px-4 py-2 text-sm text-white/80 hover:bg-white/5 hover:text-secondary-400 flex items-center space-x-2 transition-all duration-300"
                        >
                          <Settings size={14} />
                          <span>Edit Profile</span>
                        </button>
                        <hr className="my-1 border-white/10" />
                        <button
                          onClick={handleLogout}
                          className="block w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 flex items-center space-x-2 transition-all duration-300"
                        >
                          <LogOut size={14} />
                          <span>Logout</span>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex space-x-4">
                <Link
                  to="/login"
                  className="text-white/80 hover:text-secondary-400 px-3 py-2 rounded-md text-sm font-medium transition-all duration-300 hover:bg-white/5"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="bg-secondary-400/20 text-secondary-400 hover:bg-secondary-400/30 px-4 py-2 rounded-md text-sm font-medium border border-secondary-400/30 transition-all duration-300"
                >
                  Get Started
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-white/80 hover:text-secondary-400 p-2 transition-all duration-300 hover:bg-white/5 rounded-md"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className={`md:hidden border-t border-white/10 ${
            isScrolled 
              ? 'bg-black/80 backdrop-blur-md' 
              : 'bg-black/60 backdrop-blur-sm'
          }`}>
            <div className="px-2 pt-2 pb-3 space-y-1">
              <Link
                to="/"
                className="block px-3 py-2 text-base font-medium text-white hover:text-secondary-400 hover:bg-white/10 rounded-md transition-all duration-300"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <div className="flex items-center space-x-2">
                  <Home size={16} />
                  <span>Home</span>
                </div>
              </Link>
              
              {user?.userType === 'freelancer' && (
                <Link
                  to="/projects"
                  className="block px-3 py-2 text-base font-medium text-white hover:text-secondary-400 hover:bg-white/10 rounded-md transition-all duration-300"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <div className="flex items-center space-x-2">
                    <Briefcase size={16} />
                    <span>Find Projects</span>
                  </div>
                </Link>
              )}
              
              {user?.userType === 'client' && (
                <>
                  <Link
                    to="/post-project"
                    className="block px-3 py-2 text-base font-medium text-white hover:text-secondary-400 hover:bg-white/10 rounded-md transition-all duration-300"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <div className="flex items-center space-x-2">
                      <Briefcase size={16} />
                      <span>Post Project</span>
                    </div>
                  </Link>
                  <Link
                    to="/client-projects"
                    className="block px-3 py-2 text-base font-medium text-white hover:text-secondary-400 hover:bg-white/10 rounded-md transition-all duration-300"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <div className="flex items-center space-x-2">
                      <Briefcase size={16} />
                      <span>My Projects</span>
                    </div>
                  </Link>
                </>
              )}
            </div>

            {/* Mobile User Actions */}
            <div className="pt-4 pb-3 border-t border-white/10">
              {user ? (
                <div className="px-2 space-y-1">
                  <div className="px-3 py-2 text-sm text-white/80">
                    <div className="flex items-center space-x-2">
                      <User size={16} />
                      <span className="font-medium">{user.name}</span>
                      <span className="text-xs bg-secondary-400/20 text-secondary-400 px-2 py-1 rounded-full border border-secondary-400/30">
                        {user.userType}
                      </span>
                    </div>
                  </div>
                  
                  <button
                    onClick={handleViewProfile}
                    className="block w-full text-left px-3 py-2 text-base font-medium text-white/80 hover:text-secondary-400 hover:bg-white/5 rounded-md transition-all duration-300"
                  >
                    <div className="flex items-center space-x-2">
                      <User size={16} />
                      <span>View Profile</span>
                    </div>
                  </button>
                  
                  <button
                    onClick={handleEditProfile}
                    className="block w-full text-left px-3 py-2 text-base font-medium text-white/80 hover:text-secondary-400 hover:bg-white/5 rounded-md transition-all duration-300"
                  >
                    <div className="flex items-center space-x-2">
                      <Settings size={16} />
                      <span>Edit Profile</span>
                    </div>
                  </button>
                  
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-3 py-2 text-base font-medium text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-md transition-all duration-300"
                  >
                    <div className="flex items-center space-x-2">
                      <LogOut size={16} />
                      <span>Logout</span>
                    </div>
                  </button>
                </div>
              ) : (
                <div className="px-2 space-y-2">
                  <Link
                    to="/login"
                    className="block w-full text-center px-3 py-2 text-base font-medium text-white/80 hover:text-secondary-400 hover:bg-white/5 rounded-md border border-white/20 transition-all duration-300"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="block w-full text-center px-3 py-2 text-base font-medium bg-secondary-400/20 text-secondary-400 hover:bg-secondary-400/30 rounded-md border border-secondary-400/30 transition-all duration-300"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Get Started
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header; 